import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delayChildren: 0.3, staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      content: "rabemananjarafalimananastephan@gmail.com",
      href: "mailto:rabemananjarafalimananastephan@gmail.com"
    },
    {
      icon: Phone,
      title: "Téléphone",
      content: "+261 34 25 419 37",
      href: "tel:+261342541937"
    },
    {
      icon: MapPin,
      title: "Localisation",
      content: "Antananarivo, Madagascar",
      href: "#"
    }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const result = await emailjs.send(
        'service_3osj92s',
        'template_9xa1wsw',
        {
          from_name : formData.name,
          from_email: formData.email,
          subject   : formData.subject,
          message   : formData.message,
          to_name   : 'Stephan',
          to_email  : 'rabemananjarafalimananastephan@gmail.com',
          reply_to  : formData.email,
        },
        'ILAUdgwTGGQYYgT4K'
      );

      if (result.status === 200) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      {/* ← px réduit sur mobile pour éviter le débordement */}
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2
            variants={itemVariants}
            className="section-title text-center mb-16"
          >
            Contactez-moi
          </motion.h2>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">

            {/* ── Colonne gauche ── */}
            <motion.div variants={itemVariants} className="space-y-8 min-w-0">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Parlons de votre projet
                </h3>
                <p className="text-gray-600 mb-8">
                  Je suis toujours intéressé par de nouveaux projets et
                  collaborations. N'hésitez pas à me contacter pour discuter
                  de vos idées !
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info) => (
                  <motion.div
                    key={info.title}
                    className="flex items-start space-x-4 group cursor-pointer min-w-0"
                    whileHover={{ x: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Icône — flex-shrink-0 pour qu'elle ne se rétrécisse jamais */}
                    <motion.div
                      className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center text-purple-600 group-hover:scale-110 transition-transform"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <info.icon size={20} />
                    </motion.div>

                    {/* Texte — min-w-0 + break-all pour forcer le retour à la ligne */}
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-gray-800">
                        {info.title}
                      </h4>
                      {info.href.startsWith('#') ? (
                        <span className="text-gray-600 break-words">
                          {info.content}
                        </span>
                      ) : (
                        <motion.a
                          href={info.href}
                          className="text-gray-600 hover:text-purple-600 transition-colors break-all"
                        >
                          {info.content}
                        </motion.a>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                className="card p-6 bg-gradient-to-br from-purple-50 to-pink-50"
                whileHover={{ scale: 1.02 }}
              >
                <h4 className="font-semibold text-gray-800 mb-2">
                  Disponibilité
                </h4>
                <p className="text-gray-600">
                  Je suis actuellement disponible pour des projets freelance
                  et des opportunités de collaboration.
                </p>
              </motion.div>
            </motion.div>

            {/* ── Colonne droite — formulaire ── */}
            <motion.div variants={itemVariants} className="card min-w-0">
              <form onSubmit={handleSubmit} className="space-y-6">

                {/* Nom + Email — colonne unique sur mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all"
                    placeholder="Sujet du message"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all resize-none"
                    placeholder="Votre message..."
                  />
                </div>

                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center space-x-2 text-green-600 bg-green-50 p-3 rounded-lg"
                  >
                    <CheckCircle size={20} className="flex-shrink-0" />
                    <span>Message envoyé avec succès !</span>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-600 bg-red-50 p-3 rounded-lg"
                  >
                    Une erreur est survenue. Veuillez réessayer.
                  </motion.div>
                )}

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  ) : (
                    <>
                      <span className="leading-none">Envoyer le message</span>
                    </>
                  )}
                </motion.button>
              </form>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 text-center break-words">
                  <strong>Note :</strong> Vous aurez les réponses à vos messages dans votre boîte mail.
                </p>
              </div>
            </motion.div>

          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;